// Pure graph utilities for workflow DAGs (ported from the prototype).

/** All step names this step can hand control to (children). */
export function outgoing(step) {
  if (!step) return [];
  if (step.type === "endpoint_call") return [...(step.next || [])];
  if (step.type === "branch") {
    const out = (step.branches || []).map((b) => b.next);
    if (step.default_next) out.push(step.default_next);
    return out;
  }
  return [];
}

/** Map every step name → list of step names that reference it. */
export function parentsOf(steps) {
  const parents = {};
  for (const name of Object.keys(steps)) parents[name] = [];
  for (const [src, step] of Object.entries(steps)) {
    for (const child of outgoing(step)) {
      if (child in parents) parents[child].push(src);
    }
  }
  return parents;
}

/** Set of step names reachable from `start` by walking outgoing edges. */
export function reachableFrom(steps, start) {
  const seen = new Set();
  const stack = [start];
  while (stack.length > 0) {
    const node = stack.pop();
    if (seen.has(node)) continue;
    if (!(node in steps)) continue;
    seen.add(node);
    for (const child of outgoing(steps[node])) stack.push(child);
  }
  return seen;
}

/** Set of step names that can REACH `target` (target's ancestors). */
export function ancestorsOf(steps, target) {
  const parents = parentsOf(steps);
  const seen = new Set();
  const stack = [target];
  while (stack.length > 0) {
    const node = stack.pop();
    for (const p of parents[node] ?? []) {
      if (seen.has(p)) continue;
      seen.add(p);
      stack.push(p);
    }
  }
  return seen;
}

/** Returns null if acyclic, else a string describing the cycle. */
export function findCycle(definition) {
  const { steps, start_step } = definition;
  if (!steps[start_step]) return null;

  const WHITE = 0;
  const GREY = 1;
  const BLACK = 2;
  const color = {};
  for (const name of Object.keys(steps)) color[name] = WHITE;

  const cycleFromRoot = (root) => {
    if (color[root] !== WHITE) return null;
    const stack = [{ node: root, children: outgoing(steps[root]), idx: 0 }];
    color[root] = GREY;
    while (stack.length > 0) {
      const top = stack[stack.length - 1];
      if (top.idx >= top.children.length) {
        color[top.node] = BLACK;
        stack.pop();
        continue;
      }
      const child = top.children[top.idx++];
      if (!(child in color)) continue;
      if (color[child] === GREY) {
        return stack.map((f) => f.node).concat(child).join(" → ");
      }
      if (color[child] === WHITE) {
        color[child] = GREY;
        stack.push({ node: child, children: outgoing(steps[child]), idx: 0 });
      }
    }
    return null;
  };

  const cyc = cycleFromRoot(start_step);
  if (cyc) return cyc;
  for (const name of Object.keys(steps)) {
    if (color[name] === WHITE) {
      const c = cycleFromRoot(name);
      if (c) return c;
    }
  }
  return null;
}
